import { usePathname } from "next/navigation"

import { APP_HOME_SEARCH_PILLS, appHomeSearchHref } from "@/lib/app-home-search"
import { userInitiatedSignOut } from "@/lib/auth/client"
import { browserToday } from "@/lib/date-utils"
import { useTranslation } from "@/lib/i18n/i18n-context"
import { isEuRegion } from "@/lib/region"
import { openAuthDialog } from "@/lib/storage/auth-thunks"
import { useAppDispatch } from "@/lib/storage/hooks"
import { SearchTypeIcon } from "@/components/app-home-search-type-icon"
import IconChatBubble from "../icons/chat-bubble"
import Logo from "../logo"
import { Button } from "../ui/button"
import { Link } from "../ui/link"
import { Separator } from "../ui/separator"
import { Sheet, SheetBody, SheetContent } from "../ui/sheet"
import { MarketingEntryCtaButton } from "./marketing-entry-cta-button"

export function MarketingSheetNav({
  open,
  setOpen,
  isAnonymous,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  isAnonymous: boolean
}) {
  const t = useTranslation()
  const pathname = usePathname()
  const isHomepage = pathname === "/"
  const dispatch = useAppDispatch()
  return (
    <Sheet modal position="left" open={open} onOpenChange={setOpen}>
      <SheetContent size="sm">
        <SheetBody className="flex min-h-full flex-col">
          <div className="mt-4 mb-12 flex">
            <Link
              href="/welcome"
              aria-label={t("mindtripHomepage")}
              className={
                !isHomepage ? "transition-opacity hover:opacity-60" : undefined
              }
              onClick={
                isHomepage
                  ? (e) => {
                      e.preventDefault()
                      setOpen(false)
                      window.scrollTo({ top: 0, behavior: "smooth" })
                    }
                  : undefined
              }
            >
              <Logo size={32} className="pointer-events-none" />
            </Link>
          </div>
          <ul className="-mt-3 mb-5 text-md font-medium">
            <li>
              <Link
                href="/chat"
                rel="nofollow"
                className="group flex items-center gap-2.5 py-3 transition-colors hover:text-gray-11"
              >
                <IconChatBubble className="text-[1.5em]" />
                Start chatting
              </Link>
            </li>
            <li aria-hidden>
              <Separator className="my-4" />
            </li>
            {!isEuRegion() &&
              APP_HOME_SEARCH_PILLS.map((pill) => (
                <li key={pill.type}>
                  <Link
                    href={appHomeSearchHref(pill.type)}
                    className="group flex items-center gap-2.5 py-3 font-normal transition-colors hover:text-gray-11"
                  >
                    <SearchTypeIcon type={pill.type} className="-ml-1 size-7" />
                    {pill.label}
                  </Link>
                </li>
              ))}
            <li aria-hidden>
              <Separator className="my-4" />
            </li>
            <li>
              <Link
                href="/creator-program"
                className="group flex py-3 transition-colors hover:text-gray-11"
              >
                <span>
                  <span className="font-serif lowercase italic">for</span>{" "}
                  <span>Creators</span>
                </span>
              </Link>
            </li>
            <li>
              <Link
                href="/business"
                className="group flex py-3 transition-colors hover:text-gray-11"
              >
                <span>
                  <span className="font-serif lowercase italic">for</span>{" "}
                  <span>Business</span>
                </span>
              </Link>
            </li>
            <li aria-hidden>
              <Separator className="my-4" />
            </li>
            <li>
              <Link
                href="https://resources.mindtrip.ai"
                className="group flex py-3 transition-colors hover:text-gray-11"
              >
                Resources
              </Link>
            </li>
            <li>
              <Link
                href="https://resources.mindtrip.ai/travelers/help/traveler-faqs"
                className="group flex py-3 transition-colors hover:text-gray-11"
              >
                Help
              </Link>
            </li>
          </ul>

          <div className="mt-auto flex flex-col gap-2.5">
            {isAnonymous && (
              <>
                <Button
                  variant="outline"
                  size="md"
                  block
                  onClick={() => {
                    void dispatch(
                      openAuthDialog({
                        callbackUrl: "/",
                      })
                    )
                  }}
                >
                  Log in
                </Button>

                <MarketingEntryCtaButton
                  variant="primary"
                  size="md"
                  block
                  rel="nofollow"
                />
              </>
            )}
            {!isAnonymous && (
              <Button
                variant="outline"
                size="md"
                block
                onPerform={async () => void userInitiatedSignOut()}
              >
                Log out
              </Button>
            )}

            <Separator className="my-4" />
            <p className="flex flex-wrap gap-2.5 text-sm text-muted">
              <Link
                href="/privacy-policy"
                className="-m-2 p-2 hover:text-foreground"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms-of-service"
                className="-m-2 ml-2.5 p-2 hover:text-foreground"
              >
                Terms of Service
              </Link>
              <span>© {browserToday().year} Mindtrip, Inc.</span>
            </p>
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}
